import React, { Fragment, useState, useEffect, useMemo } from 'react'
import { useNavigate } from "react-router-dom"
import { Space, Switch, Tooltip, Modal, Typography } from 'antd'
import Button from "antd-button-color"
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import { ApplicationState } from '~store/index'
import NumberWithCommas from '~utils/NumberWithCommas'
import TitleBar from '~components/Elements/TitleBar'
import FormattedMessage from '~components/Elements/FormattedMessage'
import DatePickerRange from '~components/Elements/DatePickerRange'
import DateFilter from '~components/Elements/DateFilter'
import Table from '~components/Elements/Table'
import ChangePassword from '~components/Users/ChangePassword'
import AvatarMemberComponent from '~components/Elements/AvatarMember'
import Search from '~components/Elements/Search'
import View from './View'
import useDateRangeEmpty from "~hooks/useDateRangeEmpty"
import useDatePagination from "~hooks/useDatePagination"
import useFetchSubUsers from "~hooks/useFetchSubUsers"
import ServiceAPI from '~services/services'

const SubAccounts = () => {
    const authState = useSelector((state: ApplicationState) => state.auth)
    const permissions = authState.data.permissions
    const authStateUser = authState.data.user
    const [checkNotSubUser, setCheckNotSubUser] = useState<boolean>(false)

    let navigate = useNavigate();
    const routes = [
        {
            path: '',
            breadcrumbName: 'Management',
        },
        {
            path: '',
            breadcrumbName: 'Sub Accounts',
        },
    ];
    const { confirm } = Modal;
    const [switchLoading, setSwitchLoading] = useState<boolean>(false)
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState<boolean>(false);
    const toggleModalChangePassword = () => setIsChangePasswordOpen(!isChangePasswordOpen)
    const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
    const toggleView = () => setIsViewOpen(!isViewOpen)

    const [filterText, setFilterText] = useState<string>("")
    const [search, setSearch] = useState<string>("")
    const [dataSubUser, setDataSubUser] = useState<any>([])
    const [dataSource, setDataSource] = useState<any>([])
    const { pagination, setPagination } = useDatePagination()
    const { dateRange, setDateRange } = useDateRangeEmpty()
    const { data, mutate, isLoading } = useFetchSubUsers({
        page: pagination.page,
        limit: pagination.pageSize,
        startDate: dateRange.start,
        endDate: dateRange.end,
        search: search
    })

    const handleStatus = async (status: boolean, type: string, username: string) => {
        confirm({
            title: `You want to adjust your account ${type} #${username}?`,
            icon: <ExclamationCircleOutlined />,
            content: `Make sure you want to adjust your account ${type}. #${username}`,
            okText: 'Yes',
            cancelText: 'No',
            onOk() {
                (async () => {
                    let data = { lock: status }
                    const [err, res] = await to(ServiceAPI.setSubUsersRestrict(username, data))
                    if (err) {
                        toast.error(err?.message as string)
                        setSwitchLoading(false)
                    } else {
                        mutate()
                        toast.success(res.message)
                        setSwitchLoading(false)
                    }
                })();
            },
            onCancel() {
                setSwitchLoading(false)
            },
        });
    };

    const toggleRefresh2FA = async (username: string) => {
        confirm({
            title: `You want to refresh 2FA #${username}?`,
            icon: <ExclamationCircleOutlined />,
            content: `Make sure you want to refresh 2FA #${username}`,
            okText: 'Yes',
            cancelText: 'No',
            centered: true,
            onOk() {
                (async () => {
                    const [err, res] = await to(ServiceAPI.refresh2FA(username))
                    if (err) {
                        toast.error(err?.message as string)
                        setSwitchLoading(false)
                    } else {
                        mutate()
                        toast.success(res.message)
                        setSwitchLoading(false)
                    }
                })();
            },
            onCancel() {
            },
        });
    };

    const columns: any = [
        {
            title: 'Username',
            dataIndex: 'username',
            ellipsis: true,
            render: (text: any, record: any) => (
                <div className="row-member">
                    <div
                        className="text-link"
                        onClick={() => {
                            setDataSubUser(record)
                            toggleView()
                        }}>
                        <Typography.Text strong>{record.username}</Typography.Text>
                    </div>
                </div>
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            ellipsis: true,
            render: (text: any, record: any) => (
                <AvatarMemberComponent
                    fullname={`${record.name}`}
                    phone={record?.phone}
                    search={false} />
            )
        },
        {
            title: 'Lock',
            dataIndex: 'lock',
            ellipsis: true,
            // width: '90',
            align: 'center',
            render: (text: any, record: any) => {
                return (
                    <Space direction="vertical">
                        <Tooltip title="Lock/Unlock">
                            <Switch
                                checkedChildren="Lock"
                                unCheckedChildren="Unlock"
                                checked={record.lock}
                                loading={switchLoading}
                                disabled={permissions && (permissions["SUB-USER-MANAGEMENT"] === "VIEW" || permissions["SUB-USER-MANAGEMENT"] === "OFF")}
                                onChange={(e) => {
                                    setSwitchLoading(true)
                                    handleStatus(e, "Lock", record.username)
                                }} />
                        </Tooltip>
                    </Space>
                )
            }
        },
        {
            title: 'UpdatedAt',
            dataIndex: 'updatedAt',
            ellipsis: true,
            width: '140',
            render: (text: any, record: any) => (
                <div className="row-datetime">
                    <div><span>{moment(record.updatedAt).format('DD/MM/YYYY')}</span></div>
                    <div>{moment(record.updatedAt).format('HH:mm:ss')}</div>
                </div>
            )
        },
        {
            title: 'Last login',
            dataIndex: 'loginAt',
            ellipsis: true,
            width: '140',
            render: (text: any, record: any) => (
                <div className="row-datetime">
                    {record.loginAt !== "1970-01-01 07:00:00" ? (
                        <>
                            <div><span>{moment(record.loginAt).format('DD/MM/YYYY HH:mm:ss')}</span></div>
                            <div className="msn-badge badge-light mt-2">{moment(record.loginAt).fromNow()}</div>
                        </>
                    ) : '-'}
                </div>
            )
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            ellipsis: true,
            width: 140,
            hidden: permissions && (permissions["SUB-USER-MANAGEMENT"] === "VIEW" || permissions["SUB-USER-MANAGEMENT"] === "OFF"),
            render: (text: any, record: any) => (
                <Space size={0}>
                    {checkNotSubUser && (
                        <Button
                            type="danger"
                            size="small"
                            className="me-2"
                            loading={switchLoading}
                            icon={<i className="fa-solid fa-key-skeleton me-2"></i>}
                            onClick={() => {
                                // setDataAgent(record)
                                setSwitchLoading(true)
                                toggleRefresh2FA(record.username);
                            }}>
                            Refresh 2FA
                        </Button>
                    )}
                    <Button
                        type="info"
                        size="small"
                        className="me-2"
                        icon={<i className="fa-solid fa-key me-2"></i>}
                        onClick={() => {
                            setDataSubUser(record)
                            toggleModalChangePassword();
                        }}
                    >
                        Change Password
                    </Button>
                    <Button
                        type="warning"
                        size="small"
                        icon={<i className="fa-solid fa-edit me-2"></i>}
                        onClick={() => { navigate(`edit/${record.username}`) }}
                    >
                        Edit
                    </Button>
                </Space>
            )
        }
    ].filter(item => !item.hidden);


    useEffect(() => {
        setDataSource([])
        if (data !== undefined && data.totalCount > 0) {
            setDataSource(data.data)
        }
        return () => {
            setDataSource([])
        }
    }, [data, isLoading, setDataSource])

    useMemo(() => {
        mutate()
    }, [setDateRange, setPagination])// eslint-disable-line

    const handleSearch = () => {
        setSearch(filterText)
        setPagination({ page: 1, pageSize: pagination.pageSize })
        mutate()
    }

    useEffect(() => {
        if (authState && authStateUser) {
            const notSubUser = (Object.keys(authState.data).find((key) => key === "subUser" && (authStateUser.role === "MASTER" || authStateUser.role === "AGENT")) === undefined) ? true : false
            setCheckNotSubUser(notSubUser)
        }
        return () => {
            setCheckNotSubUser(false)
        }
    }, [authState, authStateUser])

    // useMemo(() => {
    //     if (searchParams.get("search")) {
    //         setFilterText(searchParams.get("search") || "")
    //         setSearchMember(searchParams.get("search") || "")
    //         mutate()
    //     }
    // }, [searchParams, setSearch])// eslint-disable-line

    return (
        <>
            <TitleBar title={<FormattedMessage id={'main.subAccounts'} defaultMessage={'Sub Accounts'} />} subTitle={`${NumberWithCommas(data?.totalCount || 0)} items`} routes={routes} />
            <div className="d-lg-flex justify-content-lg-between d-md-flex justify-content-md-between mb-4">
                <DatePickerRange onChangeDate={setDateRange} onDateActive={dateRange} funClear={true} />
                <DateFilter onChangeDate={setDateRange} onDateActive={dateRange} />
            </div>
            <div className="d-flex justify-content-start mb-4">
                <Search onChangeSearch={setFilterText} onHandleSearch={handleSearch} filterText={filterText} />
                <button onClick={handleSearch} className="btn btn-success btn-sm text-white ms-2">Search</button>
            </div>

            {permissions && permissions["SUB-USER-MANAGEMENT"] === "EDIT" && (
                <div className="row mb-md-3 align-items-md-center">
                    <div className="mb-3 mb-md-0">
                        <Button
                            type="primary"
                            className="d-block ms-auto"
                            icon={<i className="fa-solid fa-plus me-3"></i>}
                            onClick={() => navigate(`add`)}
                        >
                            Add Sub Account
                        </Button>
                    </div>
                </div>
            )}
            <div className="box-white ant-box-table">
                <Table
                    columns={columns}
                    dataSource={dataSource || []}
                    loading={isLoading}
                    totalCount={data?.totalCount || 0}
                    currentPage={pagination.page}
                    onChangePagination={setPagination}
                />
            </div>

            <ChangePassword
                isOpen={isChangePasswordOpen}
                close={() => toggleModalChangePassword()}
                onloadData={mutate}
                username={dataSubUser?.username}
                role={'subAccount'}
            />

            <View
                isOpen={isViewOpen}
                close={() => toggleView()}
                username={dataSubUser?.username}
            />
        </>
    );
};

export default SubAccounts